import { CompRef } from '@wix/document-services-types';
import {
  WidgetPointer,
  WidgetSlot,
  WidgetSlotPosition,
  WidgetSlotToMoveContent,
  PluginsEditorType,
  PluginActionContext,
  UniversalSlotPointer,
} from '@wix/editor-platform-host-integration-apis';
import { PlatformContext } from '../../../types/platformApi';
import { SlotPointer } from './slotPointer';
import { ComponentTypes } from '@wix/editor-const';

// `referenceComponentId`/`placementsSlotIds` live on the runtime CSM data but
// are missing from the WidgetPluginComponent `data` union type, so they are
// read through this narrow cast once the WIDGET_PLUGIN type is confirmed.
export type PluginCsmEssentials = {
  referenceComponentId?: string;
  placementsSlotIds?: string[];
};

type PluginEditorKind =
  | 'siteEditor' // site editors
  | 'applicationEditor'; // blocks editor

export type PluginActionFullContext = {
  biOrigin: PluginActionContext['biOrigin'];
  controlMode: PluginActionContext['controlMode'];
  editorName: PluginsEditorType;
  editorKind: PluginEditorKind;
};

export const TPA_COMPONENT_TYPES = [
  ComponentTypes.TpaWidget,
  ComponentTypes.TpaSection,
  ComponentTypes.TpaGluedWidget,
  ComponentTypes.TpaMultiSection,
];

export interface PluginHostApiImplementation {
  getSlotPlaceholderDisplayName(compRef: CompRef): string | undefined;
  getWidgetSlot(
    slotPointer: SlotPointer,
    { includePrivate }: { includePrivate: boolean },
  ): WidgetSlot;
  getWidgetSlots(
    widgetCompRef: CompRef,
    options: { includePrivate: boolean },
  ): WidgetSlot[];
  getWidgetHostRef(compRef: CompRef): CompRef;
  getWidgetHostRefBySlotContainer(slotPointer: SlotPointer): CompRef;
  populateWidgetPluginToSlot(
    slotPointer: SlotPointer,
    widgetPointer: WidgetPointer,
  ): Promise<CompRef>;
  uninstallWidgetPlugin(slotPointer: SlotPointer): void;
  getWidgetPointer(slotPointer: SlotPointer): WidgetPointer | undefined;
  /**
   * @deprecated Will be deprecated in upcoming PRs — use
   * `getUniversalSlotPointerByPluginCompRef` instead.
   */
  getSlotPlaceholderRefByContentRef(
    widgetPluginCompRef: CompRef,
  ): UniversalSlotPointer | CompRef | undefined;
  getUniversalSlotPointerByPluginCompRef(
    widgetPluginCompRef: CompRef,
  ): UniversalSlotPointer | undefined;
  isSlotPublic(slotPointer: SlotPointer): boolean;
  getSlotPosition(compRef: CompRef): WidgetSlotPosition | null;
  getSlotContentRef(slotPointer: SlotPointer): CompRef | undefined;
  getCompRefForSlotSelection(slotPointer: SlotPointer): CompRef | undefined;
  hasWidgetSlot(widgetCompRef: CompRef): boolean;
  highlightSlot(slotPointer: SlotPointer): Promise<void>;
  unhighlightSlot(slotPointer: SlotPointer): void;
  isPluginComponent(compRef: CompRef): boolean;
  isWidgetSlotPopulated(slotPointer: SlotPointer): boolean;
}

export interface PluginHostApi {
  resolveHostAPIBySlotPointer(
    slotPointer: SlotPointer,
  ): PluginHostApiImplementation;
  resolveHostAPIByWidgetCompRef(
    widgetCompRef: CompRef,
  ): PluginHostApiImplementation;
  resolveHostAPIByWidgetPluginRef(
    widgetPluginCompRef: CompRef,
  ): PluginHostApiImplementation;
  getWidgetSlot(
    slotPointer: SlotPointer,
    { includePrivate }: { includePrivate: boolean },
  ): WidgetSlot;
  getWidgetSlots(
    widgetCompRef: CompRef,
    options: { includePrivate: boolean },
  ): WidgetSlot[];
  getWidgetSlotPlacement(
    slotPointer: SlotPointer,
    { includePrivate }: { includePrivate: boolean },
  ): WidgetSlot['placement'];
  installWidgetPlugin(
    context: PlatformContext,
    slotPointer: SlotPointer,
    widgetPointer: WidgetPointer,
    actionContext: PluginActionFullContext,
  ): Promise<CompRef>;
  uninstallWidgetPlugin(
    context: PlatformContext,
    slotPointer: SlotPointer,
    actionContext: PluginActionFullContext,
  ): Promise<void>;
  getWidgetPointer(slotPointer: SlotPointer): WidgetPointer | undefined;
  /**
   * @deprecated Will be deprecated in upcoming PRs — use
   * `getUniversalSlotPointerByPluginCompRef` instead.
   */
  getSlotPlaceholderRefByContentRef(
    compRef: CompRef,
  ): UniversalSlotPointer | CompRef | undefined;
  getUniversalSlotPointerByPluginCompRef(
    compRef: CompRef,
  ): UniversalSlotPointer | undefined;
  getSlotPlaceholderDisplayName(slotCompRef: CompRef): string | undefined;
  getWidgetHostRefBySlotRef(slotCompRef: CompRef): CompRef;
  getWidgetHostRefByWidgetRef(compRef: CompRef): CompRef;
  getSlotContentRef(slotPointer: SlotPointer): CompRef | undefined;
  getSlotPosition(compRef: CompRef): WidgetSlotPosition | null;
  getCompRefForSlotSelection(slotPointer: SlotPointer): CompRef | undefined;
  hasWidgetSlot(widgetCompRef: CompRef): boolean;
  isSlotPublic(slotPointer: SlotPointer): boolean;
  getSlotIdInBlocksAppEditor(slotCompRef: CompRef): string;
  getPlacementInBlocksAppEditor(
    slotId: string,
  ): WidgetSlot['placement'] | undefined;
  getSlotInBlocksAppEditor(slotCompRef: CompRef): WidgetSlot | undefined;
  getWidgetSlotsToMoveContent(
    slotPointer: SlotPointer,
  ): WidgetSlotToMoveContent[];
  getNearestWidgetHostRef(compRef: CompRef): CompRef | undefined;
  isSlotContentPotentialMovable(slotPointer: SlotPointer): boolean;
  moveToSlot(
    context: PlatformContext,
    fromSlot: SlotPointer,
    toSlot: SlotPointer,
    actionContext: PluginActionFullContext,
  ): Promise<void>;
  highlightSlot(slotPointer: SlotPointer): Promise<void>;
  unhighlightSlot(slotPointer: SlotPointer): void;
  isWidgetSlotPopulated(slotPointer: SlotPointer): boolean;
  isPluginComponent(widgetPluginCompRef: CompRef): boolean;
  showOnPage(slotPointer: SlotPointer): Promise<void>;
}

export interface HeaderPluginsApi {
  installHeaderPlugin(
    context: PlatformContext,
    slotPointer: SlotPointer,
    widgetPointer: WidgetPointer,
    actionContext: PluginActionFullContext,
  ): Promise<CompRef>;
  isHeaderPlugin(compRef: CompRef): boolean;
}
